import { useEffect, useRef, useState } from 'react';
import { ApiResponseError } from '@app/utils/api';

interface ApiQueryState<T> {
  data: T | null;
  dataScope?: string;
  loading: boolean;
  error: Error | null;
  backendMissing: boolean;
}

interface ApiQueryOptions {
  /** @deprecated Requests are now coalesced until the active request settles. */
  maxInFlightDedupeMs?: number;
  dataScope?: string;
  preservePreviousDataOnError?: (error: unknown) => boolean;
}

const depsEqual = (left: unknown[] | null, right: unknown[]): boolean => {
  if (!left || left.length !== right.length) {
    return false;
  }
  return left.every((value, index) => Object.is(value, right[index]));
};

/**
 * Lightweight data-fetching helper tailored for the OpenAPI-generated clients.
 * The hook intentionally avoids caching to keep the control flow explicit. When
 * the backend replies with `404/501/503`, the caller can surface the "implement
 * this API in the backend" guidance requested in the requirements.
 */
export const useApiQuery = <T>(fetcher: (() => Promise<T>) | null, deps: unknown[], options: ApiQueryOptions = {}): ApiQueryState<T> => {
  // Transport timeouts belong in the fetch implementation, where they abort
  // the actual request before this hook releases its in-flight slot.
  const [state, setState] = useState<ApiQueryState<T>>({
    data: null,
    dataScope: options.dataScope,
    loading: Boolean(fetcher),
    error: null,
    backendMissing: false
  });
  const [queuedRefreshNonce, setQueuedRefreshNonce] = useState(0);
  const mountedRef = useRef(true);
  const latestFetcherRef = useRef(fetcher);
  const inFlightFetcherRef = useRef<(() => Promise<T>) | null>(null);
  const queuedRefreshRef = useRef(false);
  const activeRequestIdRef = useRef(0);
  const latestDepsRef = useRef<unknown[] | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      queuedRefreshRef.current = false;
    };
  }, []);

  useEffect(() => {
    const dataScope = options.dataScope;
    const depsChanged = !depsEqual(latestDepsRef.current, deps);
    latestDepsRef.current = deps;
    latestFetcherRef.current = fetcher;

    if (!fetcher) {
      setState((prev) => ({
        ...prev,
        data: prev.dataScope === dataScope ? prev.data : null,
        dataScope,
        loading: false
      }));
      inFlightFetcherRef.current = null;
      queuedRefreshRef.current = false;
      activeRequestIdRef.current += 1;
      return;
    }

    if (inFlightFetcherRef.current) {
      if (depsChanged || inFlightFetcherRef.current !== fetcher) {
        // Never supersede a pending request. The request-level timeout makes
        // sure a hung transport eventually settles, after which this queued
        // refresh is started exactly once with the latest fetcher.
        queuedRefreshRef.current = true;
      }
      return;
    }

    inFlightFetcherRef.current = fetcher;
    queuedRefreshRef.current = false;
    const requestId = activeRequestIdRef.current + 1;
    activeRequestIdRef.current = requestId;
    setState((prev) => ({
      data: prev.dataScope === dataScope ? prev.data : null,
      dataScope,
      loading: true,
      error: null,
      backendMissing: false
    }));

    Promise.resolve()
      .then(fetcher)
      .then((result) => {
        const isActiveRequest = latestFetcherRef.current === fetcher && activeRequestIdRef.current === requestId;
        if (inFlightFetcherRef.current === fetcher) {
          inFlightFetcherRef.current = null;
        }
        if (!mountedRef.current || !isActiveRequest) {
          if (mountedRef.current && queuedRefreshRef.current && latestFetcherRef.current) {
            queuedRefreshRef.current = false;
            setQueuedRefreshNonce((value) => value + 1);
          }
          return;
        }
        setState({ data: result, dataScope, loading: false, error: null, backendMissing: false });
        if (queuedRefreshRef.current) {
          queuedRefreshRef.current = false;
          setQueuedRefreshNonce((value) => value + 1);
        }
      })
      .catch((caught: unknown) => {
        const isActiveRequest = latestFetcherRef.current === fetcher && activeRequestIdRef.current === requestId;
        if (inFlightFetcherRef.current === fetcher) {
          inFlightFetcherRef.current = null;
        }
        if (!mountedRef.current || !isActiveRequest) {
          if (mountedRef.current && queuedRefreshRef.current && latestFetcherRef.current) {
            queuedRefreshRef.current = false;
            setQueuedRefreshNonce((value) => value + 1);
          }
          return;
        }
        const response = caught instanceof ApiResponseError ? caught.response : caught instanceof Error && 'response' in caught ? (caught as any).response : null;
        const status = response instanceof Response ? response.status : null;
        const message = caught instanceof Error ? caught.message : 'Unknown error while querying API';
        const backendMissing =
          status === 404 ||
          status === 501 ||
          status === 503 ||
          status === 502 ||
          (status === 200 && message.toLowerCase().includes('expected json response')) ||
          status === 0;
        setState((prev) => ({
          data: options.preservePreviousDataOnError?.(caught) && prev.dataScope === dataScope ? prev.data : null,
          dataScope,
          loading: false,
          error: caught instanceof Error ? caught : new Error('Unknown error while querying API'),
          backendMissing
        }));
        if (queuedRefreshRef.current) {
          queuedRefreshRef.current = false;
          setQueuedRefreshNonce((value) => value + 1);
        }
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, fetcher, queuedRefreshNonce]);

  return state;
};
